import { EditOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Divider, Flex, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { MenuProps } from 'antd/lib'
import { useState } from 'react'

import {
  useClassifierServiceDeleteCuatmClassifierElementEndpoint,
  useClassifierServiceGetCuatmClassifierElementListEndpoint,
  useClassifierServiceGetCuatmClassifierElementListEndpointKey
} from 'src/api/queries'
import { GetCuatmClassifierElementListResponseItem2 } from 'src/api/requests'
import {
  DeleteConfirm,
  message,
  PageBreadcrumbs,
  SearchBar,
  TableActionButtons
} from 'src/components'
import { useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'

import { CUATMClassifierModal } from '../components'

export const CUATMClassifierListPage = () => {
  const { t, currentLang } = useLocales()

  const pagination = useTablePagination()

  const [search, setSearch] = useState<string>()
  const [isOpen, setIsOpen] = useState(false)
  const [currentId, setCurrentId] = useState<string>()

  const queryClient = useQueryClient()

  const { data, isLoading } =
    useClassifierServiceGetCuatmClassifierElementListEndpoint({
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
      ...(!!search && {
        nameRo: currentLang.value === 'ro' ? search : undefined,
        nameRu: currentLang.value === 'ru' ? search : undefined
      })
    })

  const { mutate, isPending } =
    useClassifierServiceDeleteCuatmClassifierElementEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            useClassifierServiceGetCuatmClassifierElementListEndpointKey
          ]
        })
        message.success(t('cuatm.delete_success'))
      }
    })

  const getActionItems = (id: string): MenuProps['items'] => [
    {
      key: 'edit',
      label: t('common.edit'),
      icon: <EditOutlined />,
      onClick: () => {
        setCurrentId(id)
        onToggle()
      }
    },
    {
      key: 'divider',
      type: 'divider'
    },
    {
      key: 'delete',
      danger: true,
      label: (
        <DeleteConfirm
          title={t('cuatm.delete_title')}
          onConfirm={() =>
            mutate({
              elementId: String(id)
            })
          }
        />
      )
    }
  ]

  const columns: ColumnsType<GetCuatmClassifierElementListResponseItem2> = [
    {
      key: 'title',
      title: t('medicine_register.products.name'),
      render: ({ translation }) => translation?.[currentLang?.value]
    },
    {
      key: 'abbreviation',
      title: t('cuatm.abbreviation'),
      render: ({ abbreviation }) => abbreviation || '-'
    },
    {
      key: 'type',
      title: t('common.type'),
      render: ({ status }) => t(`localities.${status}`)
    },
    {
      key: 'actions',
      render: ({ id }) => (
        <TableActionButtons justify="flex-end" items={getActionItems(id)} />
      )
    }
  ]

  const onToggle = () => setIsOpen((prev) => !prev)

  const onCloseModal = () => {
    setCurrentId(undefined)
    onToggle()
  }

  return (
    <PageBreadcrumbs
      title={t('menu.cuatm')}
      items={[{ title: t('menu.cuatm') }]}
    >
      <Flex justify="space-between">
        <SearchBar onSearch={setSearch} />

        <Button type="primary" onClick={onToggle}>
          {t('cuatm.add_cuatm')}
        </Button>
      </Flex>

      <Divider />

      <Table
        rowKey="id"
        columns={columns}
        dataSource={data?.data}
        loading={isLoading || isPending}
        pagination={{ ...pagination, total: data?.totalCount }}
      />

      {isOpen && (
        <CUATMClassifierModal id={currentId} onCancel={onCloseModal} />
      )}
    </PageBreadcrumbs>
  )
}
